/**
 * The full-size map's step D (DIRECTION §0.11; 68.38 the echelon, 66.20 the reserve): a side's efforts in the match state —
 * the `efforts` input sets every one of the team's at once (its commander's plan now). Pure.
 *
 * An effort keeps its id and its `formedTick` while its kind, its objective and its lead stay the same (the table follows
 * it); a squad named twice, a squad not the team's, a lead not among its squads or more followers than EFFORTS.maxFollowers
 * refuse the whole input.
 */
import type { Effort, GameState, MatchInputBody } from '../types';
import { EFFORTS } from '../constants';

type EffortsBody = Extract<MatchInputBody, { kind: 'efforts' }>;

const sameObjective = (a: Effort['objective'], b: Effort['objective']): boolean => JSON.stringify(a) === JSON.stringify(b);

export function applyEfforts(state: GameState, body: EffortsBody, tick: number): { state: GameState } | { refused: string; code: string } {
  const seen = new Set<number>();
  for (const e of body.efforts) {
    if (e.squads.length === 0 || !e.squads.includes(e.lead)) return { refused: 'An effort leads with one of its squads', code: 'lead' };
    if (e.squads.length - 1 > EFFORTS.maxFollowers) return { refused: `An effort has at most ${EFFORTS.maxFollowers} squads behind its lead`, code: 'size' };
    if ((e.kind === 'reserve') !== (e.objective === null)) return { refused: 'The reserve waits at the base; every other effort has an objective', code: 'objective' };
    for (const id of e.squads) {
      const sq = state.squads?.find(s => s.id === id);
      if (!sq) return { refused: `No squad ${id}`, code: 'squad' };
      if (sq.team !== body.team) return { refused: `Squad ${id} is not team ${body.team}'s`, code: 'team' };
      if (seen.has(id)) return { refused: `Squad ${id} is in two efforts`, code: 'twice' };
      seen.add(id);
    }
  }
  const before = (state.efforts ?? []).filter(e => e.team === body.team);
  const others = (state.efforts ?? []).filter(e => e.team !== body.team);
  let nextId = (state.efforts ?? []).reduce((m, e) => Math.max(m, e.id + 1), 0);
  const used = new Set<number>();
  const mine: Effort[] = body.efforts.map(e => {
    const kept = before.find(b => !used.has(b.id) && b.kind === e.kind && b.lead === e.lead && sameObjective(b.objective, e.objective));
    if (kept) used.add(kept.id);
    return {
      id: kept ? kept.id : nextId++, team: body.team, kind: e.kind, objective: e.objective, lead: e.lead, squads: [...e.squads],
      ...(kept?.playId !== undefined ? { playId: kept.playId } : {}), formedTick: kept ? kept.formedTick : tick, by: body.by ?? 'commander',
    };
  });
  const efforts = [...others, ...mine].sort((a, b) => a.id - b.id);
  return { state: { ...state, efforts } };
}
